import React, { forwardRef, useEffect, useRef } from 'react';

const CanvasPreview = forwardRef(({ currentTime, bgMedia, lyrics, styles, isPlaying, width, height, analyserRef }, ref) => {
  const localCanvasRef = useRef(null);
  // Expose the canvas to parent for exporting
  useEffect(() => {
    if (ref) {
      ref.current = localCanvasRef.current;
    }
  }, [ref]);

  useEffect(() => {
    const canvas = localCanvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    
    // Using a separate render loop when playing allows smooth 60fps
    // without tying entirely to React state updates which might jitter
    let animationFrameId;

    const render = () => {
      // Background
      if (bgMedia.type === 'color') {
        ctx.fillStyle = bgMedia.color;
        ctx.fillRect(0, 0, width, height);
      } else if (bgMedia.type === 'image' && bgMedia.element) {
        ctx.drawImage(bgMedia.element, 0, 0, width, height);
      } else if (bgMedia.type === 'video' && bgMedia.element) {
        ctx.drawImage(bgMedia.element, 0, 0, width, height);
      }

      // Overlay Filter
      if (bgMedia.filter === 'vignette') {
        const gradient = ctx.createRadialGradient(width / 2, height / 2, 0, width / 2, height / 2, Math.max(width, height) / 1.2);
        gradient.addColorStop(0, 'rgba(0, 0, 0, 0)');
        gradient.addColorStop(1, 'rgba(0, 0, 0, 0.8)');
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, width, height);
      } else if (bgMedia.filter === 'warm-fade') {
        ctx.fillStyle = 'rgba(255, 140, 0, 0.15)';
        ctx.fillRect(0, 0, width, height);
      } else if (bgMedia.filter === 'film-grain') {
        // Fast film grain by generating once and drawing with random offsets
        if (!window.grainCanvas || window.grainCanvas.width !== width * 1.5) {
          const grainCanvas = document.createElement('canvas');
          const gw = width * 1.5;
          const gh = height * 1.5;
          grainCanvas.width = gw;
          grainCanvas.height = gh;
          const gctx = grainCanvas.getContext('2d');
          const idata = gctx.createImageData(gw, gh);
          const buffer32 = new Uint32Array(idata.data.buffer);
          const len = buffer32.length;
          for (let i = 0; i < len; i++) {
            if (Math.random() < 0.1) {
              buffer32[i] = 0x15000000; // subtle noise
            }
          }
          gctx.putImageData(idata, 0, 0);
          window.grainCanvas = grainCanvas;
        }
        
        const offsetX = -Math.random() * (width * 0.5);
        const offsetY = -Math.random() * (height * 0.5);
        ctx.drawImage(window.grainCanvas, offsetX, offsetY);
      }

      // Visualizer
      if (styles.visualizerEnabled && analyserRef && analyserRef.current) {
        const analyser = analyserRef.current;
        const bufferLength = analyser.frequencyBinCount;
        const dataArray = new Uint8Array(bufferLength);
        analyser.getByteFrequencyData(dataArray);

        ctx.save();
        ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';

        if (styles.visualizerStyle === 'bars') {
          const barWidth = (width / bufferLength) * 2.5;
          let barHeight;
          let x = 0;
          for (let i = 0; i < bufferLength; i++) {
            barHeight = (dataArray[i] / 255) * (height / 2);
            ctx.fillRect(x, height - barHeight, barWidth, barHeight);
            x += barWidth + 1;
          }
        } else if (styles.visualizerStyle === 'circle') {
          let sum = 0;
          for (let i = 0; i < bufferLength; i++) {
            sum += dataArray[i];
          }
          const average = sum / bufferLength;
          const baseRadius = height * 0.15;
          const maxRadius = height * 0.45;
          const radius = baseRadius + (average / 255) * (maxRadius - baseRadius);

          ctx.beginPath();
          ctx.arc(width / 2, height / 2, radius, 0, 2 * Math.PI);
          ctx.fill();
        }
        ctx.restore();
      }

      // Lyrics
      const activeLine = lyrics.find(l => currentTime >= l.start && currentTime <= l.end);
      
      if (activeLine) {
        ctx.font = `bold ${styles.fontSize}px "${styles.fontFamily}", sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        
        ctx.save();
        let alpha = 1;
        let scale = 1;
        const progressIntoLine = currentTime - activeLine.start;
        const totalLineDuration = activeLine.end - activeLine.start;
        
        if (styles.animationPreset === 'fade') {
          const fadeTime = 0.3;
          if (progressIntoLine < fadeTime) alpha = progressIntoLine / fadeTime;
          const timeRemaining = activeLine.end - currentTime;
          if (timeRemaining < fadeTime) alpha = Math.max(0, timeRemaining / fadeTime);
        } else if (styles.animationPreset === 'pop-in') {
          const popTime = 0.2;
          if (progressIntoLine < popTime) {
            scale = 0.8 + (progressIntoLine / popTime) * 0.2;
          }
        }
        
        ctx.globalAlpha = alpha;
        ctx.translate(width / 2, height / 2);
        ctx.scale(scale, scale);

        // Measure layout
        const spaceWidth = ctx.measureText(" ").width;
        const wordWidths = activeLine.words.map(w => ctx.measureText(w.text).width);
        const totalWidth = wordWidths.reduce((a, b) => a + b, 0) + spaceWidth * (activeLine.words.length - 1);
        
        let currentX = -totalWidth / 2;
        
        for (let i = 0; i < activeLine.words.length; i++) {
          const word = activeLine.words[i];
          const isActive = currentTime >= word.start && currentTime <= word.end;
          const isPast = currentTime > word.end;
          
          ctx.fillStyle = (isActive || isPast) ? styles.activeColor : styles.textColor;
          
          if (styles.glowEnabled) {
            ctx.shadowBlur = styles.glowBlur;
            ctx.shadowColor = styles.glowColor;
          } else {
            ctx.shadowBlur = 0;
          }

          if (styles.strokeEnabled) {
            ctx.lineWidth = styles.strokeWidth;
            ctx.strokeStyle = styles.strokeColor;
            ctx.strokeText(word.text, currentX + wordWidths[i]/2, 0);
          }
          
          ctx.fillText(word.text, currentX + wordWidths[i]/2, 0);
          currentX += wordWidths[i] + spaceWidth;
        }
        
        ctx.restore();
      }

      if (isPlaying) {
        animationFrameId = requestAnimationFrame(() => {
          // If we are relying on React state (currentTime) updating 60fps, we don't strictly need this,
          // but if React drops frames, this ensures canvas redraws anyway (though currentTime won't change).
          // For true decoupling, we'd read time directly from an audio ref.
          // In this simple architecture, we let React drive it mostly.
          render();
        });
      }
    };

    render();

    return () => {
      if (animationFrameId) cancelAnimationFrame(animationFrameId);
    };
  }, [currentTime, bgMedia, lyrics, styles, isPlaying, width, height, analyserRef]);

  // Handle video background playback sync
  useEffect(() => {
    if (bgMedia.type === 'video' && bgMedia.element) {
      if (isPlaying) {
        bgMedia.element.play().catch(e => console.log('Video play error:', e));
      } else {
        bgMedia.element.pause();
      }
      
      // Sync time occasionally if it drifts by more than 0.2s
      if (Math.abs(bgMedia.element.currentTime - currentTime) > 0.2 && !isPlaying) {
        bgMedia.element.currentTime = currentTime;
      }
    }
  }, [isPlaying, currentTime, bgMedia]);

  return (
    <div className="relative w-full max-w-[1280px] aspect-video bg-black shadow-2xl shadow-emerald-900/20 border border-gray-700 rounded overflow-hidden">
      <canvas 
        ref={localCanvasRef}
        width={width}
        height={height}
        className="w-full h-full object-contain"
      />
    </div>
  );
});

export default CanvasPreview;